import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface NavItemFlyoutProps {
  title: string;
  flagContent: ReactNode;
  children: ReactNode;
  buttonRef: React.RefObject<HTMLButtonElement>;
  onClose: () => void;
  scheduleClose: () => void;
  clearCloseTimeout: () => void;
}

export const NavItemFlyout = ({
  title,
  flagContent,
  children,
  buttonRef,
  onClose,
  scheduleClose,
  clearCloseTimeout,
}: NavItemFlyoutProps) => {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 });

  // Calculate flyout position and adjust if it overflows viewport
  useEffect(() => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const initialTop = buttonRect.top - 58;
      const left = buttonRect.right + 16;

      setFlyoutPosition({ top: initialTop, left });

      requestAnimationFrame(() => {
        if (flyoutRef.current) {
          const flyoutRect = flyoutRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const overflow = flyoutRect.bottom - viewportHeight;

          if (overflow > 0) {
            const adjustedTop = Math.max(8, initialTop - overflow - 8);
            setFlyoutPosition({ top: adjustedTop, left });
          }
        }
      });
    }
  }, [buttonRef]);

  // Find next focusable nav item, traversing across lists if needed
  const findNextNavFocusable = useCallback((): HTMLElement | null => {
    const currentLi = buttonRef.current?.closest("li");
    if (!currentLi) return null;

    let nextLi = currentLi.nextElementSibling;
    while (nextLi) {
      const focusable = nextLi.querySelector<HTMLElement>("button, a");
      if (focusable) return focusable;
      nextLi = nextLi.nextElementSibling;
    }

    const currentList = currentLi.closest("ul");
    if (!currentList) return null;

    let nextList = currentList.nextElementSibling;
    while (nextList) {
      if (nextList.tagName === "UL") {
        const focusable = nextList.querySelector<HTMLElement>("button, a");
        if (focusable) return focusable;
      }
      nextList = nextList.nextElementSibling;
    }

    return null;
  }, [buttonRef]);

  const handleFlyoutKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Tab" && !e.shiftKey) {
        const flyout = flyoutRef.current;
        if (flyout) {
          const focusables = flyout.querySelectorAll<HTMLElement>(
            'a, button, [tabindex]:not([tabindex="-1"])',
          );
          const lastFocusable = focusables[focusables.length - 1];

          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            onClose();
            const nextFocusable = findNextNavFocusable();
            if (nextFocusable) {
              nextFocusable.focus();
            }
          }
        }
      } else if (e.key === "Tab" && e.shiftKey) {
        const flyout = flyoutRef.current;
        if (flyout) {
          const focusables = flyout.querySelectorAll<HTMLElement>(
            'a, button, [tabindex]:not([tabindex="-1"])',
          );
          const firstFocusable = focusables[0];

          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            buttonRef.current?.focus();
          }
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        buttonRef.current?.focus();
      }
    },
    [buttonRef, findNextNavFocusable, onClose],
  );

  return createPortal(
    <div
      ref={flyoutRef}
      data-navitem-flyout
      className="dark:bg-nav-background fixed z-[9999] min-w-52 rounded-lg bg-gray-700 shadow-lg"
      style={{
        top: flyoutPosition.top,
        left: flyoutPosition.left,
      }}
      onMouseEnter={clearCloseTimeout}
      onMouseLeave={scheduleClose}
      onFocus={clearCloseTimeout}
      onBlur={scheduleClose}
      onKeyDown={handleFlyoutKeyDown}
      onClickCapture={() => {
        clearCloseTimeout();
        onClose();
      }}
    >
      <div className="border-b border-white/10 py-3 pr-4 pl-7">
        <span className="flex gap-2 text-white">
          {title}
          {flagContent}
        </span>
      </div>
      <div className="py-2">{children}</div>
    </div>,
    document.body,
  );
};
